import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import {
  LayoutDashboard, Search, PlusCircle, ClipboardList, Wallet, Receipt, User, LifeBuoy, Package, Users,
  ShieldCheck, BadgeCheck, Banknote, FileBarChart, Settings, ScrollText, Inbox,
} from "lucide-react";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { AppShell } from "./components/AppShell";
import { SalesIdChip, Loading } from "./components/common";
import { Login, Register, ForgotPassword, ResetPassword, Privacy } from "./pages/auth";
import ExecDashboard from "./pages/exec/Dashboard";
import FindDeals from "./pages/exec/FindDeals";
import DealDetail from "./pages/exec/DealDetail";
import BookOrder from "./pages/exec/BookOrder";
import MyOrders from "./pages/exec/MyOrders";
import MyOrderView from "./pages/exec/MyOrderView";
import MyCommissions from "./pages/exec/MyCommissions";
import PaymentHistory from "./pages/exec/PaymentHistory";
import Profile from "./pages/exec/Profile";
import Support from "./pages/exec/Support";
import AdminOverview from "./pages/admin/Overview";
import ManageDeals from "./pages/admin/ManageDeals";
import DealEditor from "./pages/admin/DealEditor";
import Executives from "./pages/admin/Executives";
import AllOrders from "./pages/admin/AllOrders";
import OrderDetail from "./pages/admin/OrderDetail";
import CommissionApprovals from "./pages/admin/CommissionApprovals";
import CommissionPayments from "./pages/admin/CommissionPayments";
import Reports from "./pages/admin/Reports";
import PlatformSettings from "./pages/admin/PlatformSettings";
import AuditLog from "./pages/admin/AuditLog";
import SupportInbox from "./pages/admin/SupportInbox";

const EXEC_NAV = [
  { to: "/app", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/app/deals", label: "Find Deals", icon: Search },
  { to: "/app/book", label: "Book an Order", icon: PlusCircle },
  { to: "/app/orders", label: "My Orders", icon: ClipboardList },
  { to: "/app/commissions", label: "My Commissions", icon: Wallet },
  { to: "/app/payments", label: "Payment History", icon: Receipt },
  { to: "/app/profile", label: "My Profile", icon: User },
  { to: "/app/support", label: "Help and Support", icon: LifeBuoy },
];
const EXEC_BOTTOM = [
  { to: "/app", label: "Home", icon: LayoutDashboard, end: true },
  { to: "/app/deals", label: "Deals", icon: Search },
  { to: "/app/book", label: "Book", icon: PlusCircle },
  { to: "/app/orders", label: "Orders", icon: ClipboardList },
  { to: "/app/commissions", label: "Earnings", icon: Wallet },
];
const ADMIN_NAV = [
  { to: "/admin", label: "Overview", icon: LayoutDashboard, end: true },
  { to: "/admin/deals", label: "Manage Deals", icon: Package },
  { to: "/admin/executives", label: "Sales Executives", icon: Users },
  { to: "/admin/orders", label: "All Orders", icon: ClipboardList },
  { to: "/admin/verification", label: "Order Verification", icon: ShieldCheck },
  { to: "/admin/approvals", label: "Commission Approvals", icon: BadgeCheck },
  { to: "/admin/payments", label: "Commission Payments", icon: Banknote },
  { to: "/admin/reports", label: "Reports and Exports", icon: FileBarChart },
  { to: "/admin/support", label: "Support Inbox", icon: Inbox },
  { to: "/admin/settings", label: "Platform Settings", icon: Settings },
  { to: "/admin/audit", label: "Audit Log", icon: ScrollText },
];

function Guard({ role, children }) {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== role) return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
  return children;
}

function Home() {
  const { user } = useAuth();
  if (user === null) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/admin" : "/app"} replace />;
}

function ExecShell() {
  const { user } = useAuth();
  return <AppShell items={EXEC_NAV} bottomItems={EXEC_BOTTOM} roleLabel="Sales Executive" header={<div className="hidden sm:block"><SalesIdChip id={user.sales_id} /></div>} />;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/app" element={<Guard role="executive"><ExecShell /></Guard>}>
            <Route index element={<ExecDashboard />} />
            <Route path="deals" element={<FindDeals />} />
            <Route path="deals/:id" element={<DealDetail />} />
            <Route path="book" element={<BookOrder />} />
            <Route path="orders" element={<MyOrders />} />
            <Route path="orders/:id" element={<MyOrderView />} />
            <Route path="commissions" element={<MyCommissions />} />
            <Route path="payments" element={<PaymentHistory />} />
            <Route path="profile" element={<Profile />} />
            <Route path="support" element={<Support />} />
          </Route>
          <Route path="/admin" element={<Guard role="admin"><AppShell items={ADMIN_NAV} roleLabel="Administrator" /></Guard>}>
            <Route index element={<AdminOverview />} />
            <Route path="deals" element={<ManageDeals />} />
            <Route path="deals/new" element={<DealEditor />} />
            <Route path="deals/:id" element={<DealEditor />} />
            <Route path="executives" element={<Executives />} />
            <Route path="orders" element={<AllOrders />} />
            <Route path="verification" element={<AllOrders queue="verification" />} />
            <Route path="orders/:id" element={<OrderDetail />} />
            <Route path="approvals" element={<CommissionApprovals />} />
            <Route path="payments" element={<CommissionPayments />} />
            <Route path="reports" element={<Reports />} />
            <Route path="support" element={<SupportInbox />} />
            <Route path="settings" element={<PlatformSettings />} />
            <Route path="audit" element={<AuditLog />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster position="top-center" richColors />
    </AuthProvider>
  );
}

export default App;
